/**
 * On the first idle tick after the shell mounts, warm the services the current
 * route is about to hit. Failures are silently swallowed, warmups never block
 * a render, and they are never issued on a route the guard is about to
 * replace — the caller checks the session first.
 */
import { apiUrl } from '@shared/api/config';

export type WarmupService =
  | 'auth'
  | 'content'
  | 'assets'
  | 'finance'
  | 'engagement'
  | 'notification'
  | 'support'
  | 'analytics'
  | 'search';

/** `auth` is always warmed; the rest follow the route the reader landed on. */
const ROUTE_SERVICES: readonly { pattern: RegExp; services: readonly WarmupService[] }[] = [
  { pattern: /^\/$/, services: ['content', 'analytics', 'search'] },
  { pattern: /^\/titles\//, services: ['content', 'finance', 'assets', 'engagement', 'analytics'] },
  { pattern: /^\/(plans|checkout|subscription)/, services: ['finance'] },
  { pattern: /^\/lists/, services: ['content', 'finance'] },
  { pattern: /^\/inbox/, services: ['notification'] },
  { pattern: /^\/cases/, services: ['support'] },
  { pattern: /^\/(profile|u)\//, services: ['content', 'engagement'] },
  { pattern: /^\/settings/, services: ['auth', 'notification'] },
];

function servicesFor(pathname: string): readonly WarmupService[] {
  const matched = ROUTE_SERVICES.find((row) => row.pattern.test(pathname));
  return ['auth', ...(matched?.services ?? [])];
}

export function warmupForRoute(pathname: string = window.location.pathname): void {
  const run = (): void => {
    for (const service of servicesFor(pathname)) {
      void fetch(apiUrl(`/warmup?service=${service}`), { method: 'GET', credentials: 'include' }).catch(
        () => {
          /* a warmup that fails is a warmup that did not happen — nothing else */
        },
      );
    }
  };

  // `requestIdleCallback` is not on every engine Safari ships, so the fallback
  // is a timer. The capability test is read off a local alias rather than
  // narrowing `window` itself, which TypeScript reduces to `never` in the
  // else-branch of an `in` check against a global.
  const idle = window.requestIdleCallback;
  if (typeof idle === 'function') {
    idle.call(window, run, { timeout: 3000 });
    return;
  }
  window.setTimeout(run, 1500);
}
